import { ensurePlatformAnalyticsSchema } from "./server-platform-analytics-bootstrap.js";
import type { RawSqlExecutor } from "./database.js";

type AnalyticsSqlClient = {
  unsafe(query: string, params?: any[]): Promise<Iterable<unknown>>;
  end?: () => Promise<void> | void;
};

type AnalyticsSqlConnector = (url: string) => Promise<AnalyticsSqlClient>;
type AnalyticsSchemaEnsurer = (params: { rawSql: RawSqlExecutor }) => Promise<void>;

export type AnalyticsSqlRuntimeSource = "platform-fallback" | "dedicated-postgres" | "unavailable";

export type AnalyticsSqlRuntime = {
  rawSql: RawSqlExecutor;
  source: AnalyticsSqlRuntimeSource;
  close(): Promise<void>;
};

function isDisabledAnalyticsEnv(env: NodeJS.ProcessEnv): boolean {
  const enabled = env.GENCOW_ANALYTICS_ENABLED?.trim().toLowerCase();
  const backend = env.GENCOW_ANALYTICS_BACKEND?.trim().toLowerCase();
  return ["0", "false", "off", "disabled", "no"].includes(enabled ?? "") || backend === "disabled";
}

function redactPostgresDsn(value: string): string {
  return value.replace(/postgres(?:ql)?:\/\/[^\s"']+/gi, "postgres://[redacted]");
}

async function connectPostgresAnalytics(url: string): Promise<AnalyticsSqlClient> {
  const mod = await import("postgres");
  return mod.default(url, {
    max: Number(process.env.GENCOW_ANALYTICS_DB_POOL_MAX || 3),
    idle_timeout: Number(process.env.GENCOW_ANALYTICS_DB_IDLE_TIMEOUT_SEC || 20),
    connect_timeout: Number(process.env.GENCOW_ANALYTICS_DB_CONNECT_TIMEOUT_SEC || 5),
  });
}

function createUnavailableRawSql(reason: string): RawSqlExecutor {
  return (async () => {
    throw new Error(`analytics database unavailable: ${redactPostgresDsn(reason).slice(0, 160)}`);
  }) as RawSqlExecutor;
}

export async function createAnalyticsSqlRuntime(params: {
  fallbackRawSql: RawSqlExecutor;
  env?: NodeJS.ProcessEnv;
  logger?: Pick<Console, "log" | "warn">;
  connect?: AnalyticsSqlConnector;
  ensureSchema?: AnalyticsSchemaEnsurer;
}): Promise<AnalyticsSqlRuntime> {
  const env = params.env ?? process.env;
  const logger = params.logger ?? console;
  const analyticsDatabaseUrl = env.ANALYTICS_DATABASE_URL?.trim();

  if (!analyticsDatabaseUrl || isDisabledAnalyticsEnv(env)) {
    return {
      rawSql: params.fallbackRawSql,
      source: "platform-fallback",
      close: async () => {},
    };
  }

  const connect = params.connect ?? connectPostgresAnalytics;
  const ensureSchema = params.ensureSchema ?? ensurePlatformAnalyticsSchema;
  let client: AnalyticsSqlClient | null = null;

  try {
    client = await connect(analyticsDatabaseUrl);
    const rawSql = (async (query: string, sqlParams?: unknown[]): Promise<unknown[]> => {
      const result =
        sqlParams && sqlParams.length > 0
          ? await client!.unsafe(query, sqlParams as any[])
          : await client!.unsafe(query);
      return Array.from(result);
    }) as RawSqlExecutor;

    await ensureSchema({ rawSql });
    logger.log("[analytics] using dedicated Postgres analytics store (ANALYTICS_DATABASE_URL configured)");

    return {
      rawSql,
      source: "dedicated-postgres",
      close: async () => {
        await client?.end?.();
      },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    try {
      await client?.end?.();
    } catch {
      // Ignore close failures while falling back to fail-open analytics behavior.
    }
    logger.warn(
      `[analytics] dedicated analytics DB unavailable; collector/provider will fail open: ${redactPostgresDsn(message).slice(0, 160)}`,
    );
    return {
      rawSql: createUnavailableRawSql(message),
      source: "unavailable",
      close: async () => {},
    };
  }
}
